import { useCallback, useEffect, useMemo } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import humps from 'humps'
import { useAppDispatch, useAppSelector } from '@verzekeringapp-mono/shared/src/hooks/state'
import useRelationMutation from '@verzekeringapp-mono/shared/src/hooks/use_relation_mutation'
import {
  getResourceAuthorization,
  selectEffectiveTogglesForCurrent
} from '@verzekeringapp-mono/shared/src/state/selectors/resource_authorization'
import {
  pruneExpiredOverrides,
  RelationToggleKey,
  setLocalRelationToggles
} from '@verzekeringapp-mono/shared/src/state/slices/resource_authorization_slice'
import { getThemeMode } from '@verzekeringapp-mono/shared/src/state/selectors/setup'
import { setThemeMode } from '@verzekeringapp-mono/shared/src/state/slices/setup_slice'
import { ThemeMode } from '@verzekeringapp-mono/shared/src/utils/colors'
import { isMainUser } from '@verzekeringapp-mono/shared/src/utils/resource_utils'
import { preferenceRoutes, PreferenceRouteId } from '~/screens/profile/preferences_screen/preferences_config'
import { useThemeColorContext } from '~/providers/ThemeColorProvider'
import { logEvent } from '~/utils/firebase'

type PreferenceStatus = {
  label: string
  tone: 'success' | 'danger' | 'neutral'
}

export type ThemePreferenceMode = ThemeMode | 'system'

type RelationToggles = {
  cooperationMembership: boolean
  customerPanel: boolean
  zestMagazine: boolean
}

const relationToggleKeys: RelationToggleKey[] = [
  'cooperationMembership',
  'zestMagazine',
  'customerPanel'
]

const zestBlockedMessage = 'Activeer eerst je lidmaatschap van de coöperatie Nh1816. Daarna kun je je voorkeur voor Zest Magazine aanpassen.'

const usePreferencesController = () => {
  const dispatch = useAppDispatch()
  const location = useLocation()
  const navigate = useNavigate()
  const { themeMode } = useThemeColorContext()
  const currentResourceAuthorization = useAppSelector(getResourceAuthorization)
  const relationToggles = useAppSelector(selectEffectiveTogglesForCurrent)
  const selectedThemeMode = useAppSelector(getThemeMode)
  const { requestRelationMutation } = useRelationMutation()

  const mainUser = useMemo(
    () => isMainUser(currentResourceAuthorization?.roles),
    [currentResourceAuthorization?.roles]
  )

  useEffect(() => {
    const displayPath = preferenceRoutes.display.path
    const preferencesPath = '/mijn-profiel/voorkeuren'
    const isDisplayPreference = location.pathname === preferencesPath || location.pathname === displayPath

    if (currentResourceAuthorization?.roles && !mainUser && !isDisplayPreference) {
      navigate('/mijn-profiel')
    }
  }, [currentResourceAuthorization?.roles, location.pathname, mainUser, navigate])

  useEffect(() => {
    dispatch(pruneExpiredOverrides())
  }, [dispatch])

  const updateRelationPreferences = useCallback(async (patch: Partial<RelationToggles>) => {
    if (!relationToggles) {
      return false
    }

    const refKey = currentResourceAuthorization?.relationInfo?.agentNumber
    const relationNumber = currentResourceAuthorization?.relationInfo?.relationNumber

    if (!refKey || !relationNumber) {
      window.alert('Er is iets misgegaan. Probeer het later opnieuw.')

      return false
    }

    const nextToggles: RelationToggles = {
      cooperationMembership: patch.cooperationMembership ?? relationToggles.cooperationMembership,
      zestMagazine: patch.zestMagazine ?? relationToggles.zestMagazine,
      customerPanel: patch.customerPanel ?? relationToggles.customerPanel
    }

    if (!nextToggles.cooperationMembership) {
      nextToggles.zestMagazine = false
    }

    relationToggleKeys.forEach((key) => {
      if (relationToggles[key] !== nextToggles[key]) {
        logEvent(`${humps.decamelize(key)}_${nextToggles[key] ? 'enabled' : 'disabled'}`)
      }
    })

    dispatch(
      setLocalRelationToggles({
        relationNumber: relationNumber.toString(),
        toggles: nextToggles
      })
    )

    try {
      await requestRelationMutation(refKey, relationNumber, nextToggles)
    } catch (error) {
      // Keep the optimistic local state. Existing pending-mutation sync handles reconciliation.
    }

    return true
  }, [currentResourceAuthorization, dispatch, relationToggles, requestRelationMutation])

  const setCooperationMembership = useCallback(async (value: boolean) => {
    await updateRelationPreferences({ cooperationMembership: value })
  }, [updateRelationPreferences])

  const setZestMagazine = useCallback(async (value: boolean) => {
    await updateRelationPreferences({ zestMagazine: value })
  }, [updateRelationPreferences])

  const setCustomerPanel = useCallback(async (value: boolean) => {
    await updateRelationPreferences({ customerPanel: value })
  }, [updateRelationPreferences])

  const setThemePreference = useCallback((value: ThemePreferenceMode) => {
    dispatch(setThemeMode(value === 'system' ? undefined : value))
  }, [dispatch])

  const ensureZestAccessible = useCallback((options?: { navigateOnBlock?: boolean }) => {
    if (relationToggles?.cooperationMembership) {
      return true
    }

    window.alert(zestBlockedMessage)

    if (options?.navigateOnBlock) {
      navigate('/mijn-profiel/voorkeuren')
    }

    return false
  }, [navigate, relationToggles?.cooperationMembership])

  const openPreference = useCallback((preference: PreferenceRouteId) => {
    if (preference === 'zest-magazine' && !ensureZestAccessible()) {
      return
    }

    navigate(preferenceRoutes[preference].path)
  }, [ensureZestAccessible, navigate])

  const zestMagazineEnabled = Boolean(
    relationToggles?.cooperationMembership && relationToggles?.zestMagazine
  )

  const statuses = useMemo(() => ({
    cooperationMembership: {
      label: relationToggles?.cooperationMembership ? 'Aan' : 'Uit',
      tone: relationToggles?.cooperationMembership ? 'success' : 'danger'
    } satisfies PreferenceStatus,
    zestMagazine: {
      label: zestMagazineEnabled ? 'Aan' : 'Uit',
      tone: zestMagazineEnabled ? 'success' : 'danger'
    } satisfies PreferenceStatus,
    customerPanel: {
      label: relationToggles?.customerPanel ? 'Aan' : 'Uit',
      tone: relationToggles?.customerPanel ? 'success' : 'danger'
    } satisfies PreferenceStatus,
    display: {
      label: themeMode === 'light' ? 'Licht' : 'Donker',
      tone: 'neutral'
    } satisfies PreferenceStatus
  }), [
    relationToggles?.cooperationMembership,
    relationToggles?.customerPanel,
    themeMode,
    zestMagazineEnabled
  ])

  return {
    relationToggles,
    statuses,
    mainUser,
    openPreference,
    display: {
      selectedMode: selectedThemeMode ?? 'system',
      setMode: setThemePreference,
      status: statuses.display
    },
    ensureZestAccessible,
    setCooperationMembership,
    setCustomerPanel,
    setZestMagazine
  }
}

export default usePreferencesController
